import React from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { Home as HomeIcon, Building2, PhoneCall, Shield, Stethoscope, HeartHandshake, Calculator, GraduationCap, Truck, Package, Calendar, ArrowRight, Zap, UserCheck, BadgeIndianRupee, Scale, Headset, Puzzle } from 'lucide-react'
import HeroSlider from '../components/HeroSlider'

const Home = () => {
  React.useEffect(() => {
    document.title = "Shrijaa Outsourcing Consultants - Workforce Solutions & Financial Services";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute("content", "Shrijaa Outsourcing Consultants provides professional manpower solutions, staffing services, business loans, MSME loans, home loans, personal loans, and insurance products in Hyderabad.");
    }

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', window.location.origin + '/');
  }, []);

  const stats = [
    { number: '300+', label: 'Successful Placements' },
    { number: '35+', label: 'Happy Clients' },
    { number: '1+', label: 'Years Experience' },
    { number: '6+', label: 'Service Categories' }
  ]

  const services = [
    {
      icon: <HomeIcon className="w-7 h-7" />,
      title: 'Housekeeping',
      description: 'Professional housekeeping services for residential & commercial.',
      href: '/services/housekeeping',
      image: '/service-housekeeping.webp'
    },
    {
      icon: <Building2 className="w-7 h-7" />,
      title: 'Office Boys/Girls',
      description: 'Reliable office support staff for various tasks.',
      href: '/services/office-boys',
      image: '/service-officeboys.webp'
    },
    {
      icon: <PhoneCall className="w-7 h-7" />,
      title: 'Telecallers',
      description: 'Professional telecallers for outbound and inbound calling.',
      href: '/services/telecallers',
      image: '/service-telecallers.avif'
    },
    {
      icon: <Shield className="w-7 h-7" />,
      title: 'Security & Watchman',
      description: 'Watchman services with accommodation available.',
      href: '/services/watchman',
      image: '/service-security.webp'
    },
    {
      icon: <Stethoscope className="w-7 h-7" />,
      title: 'Ward Boys/Girls',
      description: 'Hospital ward staff for patient care.',
      href: '/services/ward-boys',
      image: '/service-wardboys.webp'
    },
    {
      icon: <HeartHandshake className="w-7 h-7" />,
      title: 'Care Takers',
      description: 'Trained care takers for home support and daily assistance.',
      href: '/services/caretakers',
      image: '/service-care-takers.avif'
    },
    {
      icon: <Calculator className="w-7 h-7" />,
      title: 'Accounting Jobs',
      description: 'Qualified accounting professionals.',
      href: '/services/accounting-jobs',
      image: '/service-accounting.webp'
    },
    {
      icon: <GraduationCap className="w-7 h-7" />,
      title: 'Any Degree Jobs',
      description: 'Job placement for all degree holders.',
      href: '/services/any-degree-jobs',
      image: '/service-anydegree.webp'
    },
    {
      icon: <Truck className="w-7 h-7" />,
      title: 'Drivers',
      description: 'Professional drivers for all vehicles.',
      href: '/services/drivers',
      image: '/service-drivers.webp'
    },
    {
      icon: <Package className="w-7 h-7" />,
      title: 'Delivery Boys',
      description: 'Delivery personnel for courier & food.',
      href: '/services/delivery-boys',
      image: '/service-delivery.webp'
    },
    {
      icon: <Calendar className="w-7 h-7" />,
      title: 'Event Services',
      description: 'Complete staffing for events & functions.',
      href: '/services/event-services',
      image: '/service-events.webp'
    }
  ]


  return (
    <div>
      {/* Hero Slider */}
      <HeroSlider />

      {/* Stats Section */}
      <section className="bg-gradient-to-r from-primary-700 to-teal-700 py-8 md:py-12">
        <div className="container-max">
          <div className="grid grid-cols-4 md:gap-8 gap-2 text-center text-white">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="text-2xl md:text-5xl font-bold mb-1">{stat.number}</div>
                <div className="text-primary-200 text-[10px] md:text-sm uppercase tracking-wider leading-tight">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Talent Highlight Section - ManpowerGroup Style */}
      <section className="section-padding bg-white">
        <div className="container-max">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <p className="text-accent-500 font-semibold uppercase tracking-wider text-sm mb-3">Why Choose Us</p>
                <h2 className="text-3xl md:text-4xl font-bold text-primary-800 mb-6 leading-tight">
                  Connecting the Right Talent with the Right Opportunities
                </h2>
                <p className="text-secondary-600 mb-8 leading-relaxed">
                  Shrijaa Outsourcing Consultants is a professionally managed organization offering comprehensive Workforce Solutions and Financial Services, delivering integrated business support to organizations and individuals through ethical practices, industry expertise, and customer-centric solutions.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link to="/contact" className="btn-primary">
                    Find Skilled Talent →
                  </Link>
                  <Link to="/careers" className="btn-secondary">
                    Find Work
                  </Link>
                </div>
              </motion.div>
            </div>
            <div className="flex justify-center">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                className="relative"
              >
                <div className="bg-gradient-to-br from-primary-100 to-teal-100 rounded-2xl p-8 md:p-12">
                  <div className="text-center">
                    <div className="text-7xl md:text-8xl font-bold text-primary-700 mb-2">80%</div>
                    <p className="text-xl md:text-2xl font-semibold text-primary-800 mb-4">
                      of employers can't find the talent they need
                    </p>
                    <p className="text-secondary-500">
                      We bridge the gap with verified, skilled manpower solutions.
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section-padding bg-secondary-50">
        <div className="container-max">
          <div className="text-center mb-14">
            <p className="text-accent-500 font-semibold uppercase tracking-wider text-sm mb-3">What We Do</p>
            <h2 className="section-title">
              A Suite of Comprehensive Workforce Solutions
            </h2>
            <p className="section-subtitle">
              We offer end-to-end manpower and staffing solutions tailored for your organization's growth.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.04 }}
                viewport={{ once: true }}
              >
                <Link
                  to={service.href}
                  className="group block bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 h-full border border-secondary-100"
                >
                  <div className="relative overflow-hidden">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-44 object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-primary-900/40 to-transparent"></div>
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-primary-600">{service.icon}</span>
                      <h3 className="text-base font-bold text-primary-800 group-hover:text-accent-500 transition-colors">
                        {service.title}
                      </h3>
                    </div>
                    <p className="text-secondary-500 text-sm mb-3">{service.description}</p>
                    <span className="text-accent-500 text-sm font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
                      Learn More <ArrowRight size={14} />
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Financial Services Section */}
      <section className="section-padding bg-gradient-to-br from-primary-50 via-white to-teal-50 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-accent-100 rounded-full filter blur-3xl opacity-35 -translate-y-1/2 translate-x-1/2"></div>
        <div className="container-max relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Image Container */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
              className="lg:col-span-5 relative group"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-accent-500 to-primary-600 rounded-3xl transform rotate-3 scale-102 opacity-20 group-hover:rotate-1 group-hover:scale-105 transition-transform duration-500"></div>
              <img
                src="/service-financial.png"
                alt="Shrijaa Premium Financial Services"
                className="rounded-3xl shadow-2xl relative z-10 object-cover w-full h-[400px] border border-secondary-200"
              />
            </motion.div>

            {/* Content Container */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
              className="lg:col-span-7 flex flex-col justify-center"
            >
              <span className="text-accent-500 font-bold uppercase tracking-wider text-sm px-3.5 py-1.5 bg-accent-50 rounded-full w-fit">
                New Offerings
              </span>
              <h2 className="text-3xl md:text-5xl font-black text-secondary-900 mt-5 mb-6 leading-tight">
                Premium Financial &amp; Credit Solutions
              </h2>
              <p className="text-lg text-secondary-600 mb-8 leading-relaxed">
                Unlock customized credit products, loans, and protection plans designed to fuel personal aspirations and business operations. Partner with us for competitive rates, fast processing, and absolute transparency.
              </p>

              {/* Service tags grid */}
              <div className="grid grid-cols-2 gap-4 mb-8">
                {[
                  'Business & MSME Loans',
                  'Home & Auto Loans',
                  'Personal & Gold Loans',
                  'Education Loans',
                  'Premium Credit Cards',
                  'Life & General Insurance'
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2.5 text-secondary-700">
                    <div className="w-5 h-5 rounded-full bg-accent-100 flex items-center justify-center text-accent-600 shrink-0">
                      ✓
                    </div>
                    <span className="font-semibold text-[15px]">{item}</span>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/financial-services"
                  className="bg-gradient-to-r from-accent-500 to-accent-600 hover:from-accent-600 hover:to-accent-700 text-white font-bold py-3.5 px-8 rounded-xl transition-all duration-200 shadow-lg text-center"
                >
                  Explore Financial Services &rarr;
                </Link>
                <a
                  href="https://wa.me/917989308337?text=Hello!%20I%20am%20interested%20in%20Shrijaa%20Financial%20Services.%20Please%20share%20details."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white border border-secondary-300 hover:bg-secondary-50 text-secondary-700 font-bold py-3.5 px-8 rounded-xl transition-all duration-200 text-center"
                >
                  Talk to a Consultant
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="section-padding bg-white">
        <div className="container-max">
          <div className="text-center mb-14">
            <p className="text-accent-500 font-semibold uppercase tracking-wider text-sm mb-3">Our Advantages</p>
            <h2 className="section-title">
              Why Organizations Trust Shrijaa
            </h2>
          </div>

          <div className="flex md:grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 overflow-x-auto snap-x snap-mandatory scrollbar-hide pb-4 md:pb-0 -mx-4 px-4 md:mx-0 md:px-0 md:overflow-visible">
            {[
              {
                icon: <Zap className="w-10 h-10" />,
                title: 'Quick turnaround time',
                description: 'Fast mobilization aligned with your timelines, with options for urgent staffing when you need people on the floor sooner.'
              },
              {
                icon: <UserCheck className="w-10 h-10" />,
                title: 'Verified candidate database',
                description: 'Pre-screened, background-checked professionals ready to match your role, skills, and compliance expectations.'
              },
              {
                icon: <BadgeIndianRupee className="w-10 h-10" />,
                title: 'Transparent pricing',
                description: 'Clear quotes and fee structures with no surprise charges—know what you pay before you commit.'
              },
              {
                icon: <Scale className="w-10 h-10" />,
                title: 'Statutory compliance expertise',
                description: 'Guidance on PF, ESI, labor regulations, and documentation so your workforce stays compliant.'
              },
              {
                icon: <Headset className="w-10 h-10" />,
                title: 'Dedicated relationship manager',
                description: 'A single point of contact who knows your account, coordinates requirements, and follows through on every placement.'
              },
              {
                icon: <Puzzle className="w-10 h-10" />,
                title: 'Customized workforce solutions',
                description: 'Flexible models—temporary, permanent, or project-based—shaped around how your organization actually works.'
              }
            ].map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="min-w-[85%] md:min-w-0 snap-center bg-gradient-to-br from-primary-50 to-white rounded-xl p-8 text-center border border-primary-100 hover:shadow-lg transition-shadow"
              >
                <div className="text-primary-600 mb-4 flex justify-center">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-primary-800 mb-3">
                  {feature.title}
                </h3>
                <p className="text-secondary-500">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

    </div>
  )
}

export default Home
